import { z } from "zod";

/**
 * A drawn scene (ADR 0050): a shot the renderer draws instead of a picture. The model picks a kind from this library
 * and fills its props with words, numbers and choices from a list — never a colour, a position, a font or code. The
 * brand's palette colours every scene, and each component fits the words it is given, so nothing here can overflow a
 * frame. A new kind is a schema here, a component in `apps/render` and a sample in `SCENE_SAMPLES`, in one commit.
 *
 * Fields that may be empty are `nullable`, not optional: the same schema is the model's structured output, and a
 * field that is always present is one the model never forgets.
 */

/**
 * How a drawn video looks as a whole (2026-09-25): every scene of it drawn in one style, so two videos do not look like
 * the same template in different words. The model is told the style and writes to it; the renderer draws every scene
 * kind in it. `clean` is what every drawn video looked like before. The three after Showreel are made of its pieces
 * in skins of their own (2026-09-29, ADR 0061), appended so every style there was keeps its place.
 */
export const VideoStyle = z.enum(["clean", "bold", "storybook", "notebook", "neon", "retro", "showreel", "editorial", "poster", "handmade"]);
export type VideoStyle = z.infer<typeof VideoStyle>;

/**
 * The styles as a person picks one: a name, one line on how it looks and what it suits, and the made-up business its
 * example video in the gallery is for (`pnpm --filter @joogoos/render gallery` draws them).
 */
export const VIDEO_STYLES: ReadonlyArray<{ id: VideoStyle; name: string; look: string; suits: string; sample: string }> = [
  { id: "clean", name: "Clean", look: "your colours · calm cards", suits: "anything, and a first video", sample: "a dental practice" },
  { id: "bold", name: "Bold type", look: "huge words · hard cuts", suits: "an offer, an opening, a price", sample: "a gym" },
  { id: "storybook", name: "Storybook", look: "painted scenes · brush lettering", suits: "a story, a place, a day in the life", sample: "a bakery" },
  { id: "notebook", name: "Notebook", look: "paper · marker · doodles", suits: "tips, how it works, a plan", sample: "a plumber" },
  { id: "neon", name: "Neon night", look: "dark · glowing · a grid", suits: "evenings, events, tech", sample: "a cocktail bar" },
  { id: "retro", name: "Retro print", look: "cream paper · halftone · serif", suits: "craft, food, something with a past", sample: "a florist" },
  { id: "showreel", name: "Showreel", look: "kinetic type · one bar through every cut", suits: "a launch, a bold promise, how it works", sample: "a bike repair shop" },
  // Siblings of Clean, Bold type and Notebook, drawn by the Showreel pack (2026-09-29, ADR 0061). Not "Calm": Clean's
  // tile already reads "calm cards".
  { id: "editorial", name: "Editorial", look: "quiet type · a fine rule through every cut", suits: "a clinic, a studio, a considered service", sample: "a physiotherapy clinic" },
  { id: "poster", name: "Poster", look: "heavy type · a hard block through every cut", suits: "a deal, a late opening, a price that shouts", sample: "a late-night pizza takeaway" },
  { id: "handmade", name: "Handmade", look: "hand-lettered · a marker line through every cut", suits: "a local shop, a craft, a friendly how-to", sample: "a dog groomer" },
];

/** The styles a painted scene belongs in; in the others it is drawn as a headline of its caption. */
export const PAINTED_STYLES: readonly VideoStyle[] = ["clean", "storybook"];

/**
 * The styles made only of showreel pieces (2026-09-29, ADR 0061): Showreel, and three siblings of Clean, Bold type and
 * Notebook drawn by the same pack in skins of their own. A video in one is pieces from start to end: no captions, the
 * voice saying the lines, the owner's photos shown in photo pieces and nowhere else, and no animation over its opening
 * piece. Asked by what a style is made of, not by its name, so a fourth sibling is one more entry here.
 */
export const PIECE_STYLES: readonly VideoStyle[] = ["showreel", "editorial", "poster", "handmade"];
export const piecesStyle = (style?: VideoStyle) => style !== undefined && PIECE_STYLES.includes(style);

// The story first, then the pieces it needs (2026-09-29): a guide that named every piece in the library's order
// between a hero and a close, four to seven of them, had the model use all six in turn, and a video about one
// business came out as the same sequence as another's. The work itself is a story only the owner's photos tell
// (2026-09-29, ADR 0058 point 11), offered without naming its piece, which the scene guide does, so this still names
// no running order of them.
const PIECES_GUIDE = "A showreel: every scene a showreel piece and nothing else. Choose the story before the pieces. Read the brief's idea first and the page second, and decide the one story this video tells: the same business tells a different story for a different idea. When the idea names no angle (a showreel \"about us\"), take it from what the page says the business wants more of and what its customers say: weigh two or three angles and keep the one a stranger would stop for. Stories a showreel tells well, in no order of preference, each only when the idea and the page have what it needs: a promise and its proof (one thing the page promises, and the fact on the page that backs it); how it works (what happens, in order, from the customer's first step to the result); one number (a single figure the page gives, and what it means to the customer); reasons to choose it (a few short things the page says the customer gets, or does not have to do); a problem and its fix (what goes wrong for the customer, and how this business puts it right); before and after (what changes for the customer); an offer (a deal or a price the page states, and what is in it); what a check turns up (only for a business whose work is checking, inspecting or fixing things); the work itself (only when the owner chose photos for this video: what the business does, makes or looks after, shown in their own photos). Or a story of your own when none of these is this idea. Write it in \"premise\", before any shot: its shape in a few words, a colon, then its beats in order in plain words, one beat for each piece, separated by semicolons, in at most 300 characters. \"coreMessage\" is the one thing a viewer should remember, from the brief's idea rather than the business as a whole, in one sentence of at most 70 characters that the page stands behind. Then give each beat the piece that carries it, as the library says what each piece is for: a piece is there for a beat, never because it is in the library. Leave out every piece the story does not need (most videos leave out two or three), and use one twice when the story needs it (two heroes for a problem and its answer, two flips for six short facts). Open on a hero, the only piece whose first frame already shows its words: it says the viewer's own moment or problem, or the most surprising true thing on the page, never the business's name or a promise any business could make. End on one close; its address (a website or a phone number) stays on screen and is never said, so the voice says only its name and its button. A piece needs about 2.5 seconds, 3.7 for a reveal or steps of three lines. With a voice, the voice reads each piece's lines word for word (a figure said where it stands), so the lines are the whole script: write lines that read well aloud (the shortest statements too, as a person would say them), about 2 words a second of the piece and fewer for short statements, since every full stop is a pause, and let a shot's dialogue simply repeat its lines, since it is replaced by them. The voice reads every line, so all the lines of the video together, read aloud, must fit its length at about 2 words a second of it: 36 words for 18 seconds.";

/** What each style asks of the script, told to the model with the scene library. */
export const STYLE_GUIDE: Record<VideoStyle, string> = {
  clean: "Calm and clear: one idea per scene, sentences a person would say.",
  bold: "Short and loud: headlines of two to six words, more and shorter scenes (1.5 to 2.5 s), a big number or a price when the page has one. No painted scenes.",
  storybook: "A story in pictures: open and close on painted scenes, a beginning, a turn and an end.",
  notebook: "Written like notes: steps, a short list, a document with what is wrong in it, friendly words. No painted scenes.",
  neon: "Night and energy: short lines, a big number, an offer that feels like an event. No painted scenes.",
  retro: "Warm and crafted: a line with some history in it, the document or steps like a printed card. No painted scenes.",
  showreel: PIECES_GUIDE,
  // The same pieces and the same guide, each with one sentence of its own voice after it (2026-09-29, ADR 0061). It
  // travels in the script's own message, which names the style; the cached scene guide is the same for all four.
  editorial: `${PIECES_GUIDE} The words are calm and exact, as a careful professional says them: plain statements, never shouted, no exclamation marks; prefer a promise and its proof, how it works, or what a check turns up.`,
  poster: `${PIECES_GUIDE} The words are loud and few: lines of two to five words that land like a slogan, flips of one or two words each, a price or a figure whenever the page states one; the fewest words that still say it.`,
  handmade: `${PIECES_GUIDE} The words are warm and friendly, like a note the owner wrote by hand: everyday words, a little humour when the page has some, how it works in the customer's own terms; never corporate.`,
};

/**
 * How many pieces a Showreel video of this length is asked for (2026-09-29): enough that none stands still for more
 * than about 4.5 s, and few enough that each has about 3.6 s to land in. It is not the plan's shot count, whose cap of
 * six would ask a 30 s video to hold every piece for five seconds; below about 23 s its most is the plan's own count.
 */
export function showreelCount(durationMs: number): { least: number; most: number } {
  const most = Math.max(3, Math.min(12, Math.round(durationMs / 3600)));
  return { least: Math.min(most, Math.max(3, Math.ceil(durationMs / 4500))), most };
}

/**
 * The tempo of each style's music, in beats a minute (ADR 0054, 0058): the worker's score plays at it, and a showreel
 * piece puts its impacts on its beat, so the two read it from here and cannot drift apart. The piece styles added on
 * 2026-09-29 (ADR 0061) play at whole frames a beat at 30 fps, 18, 15 and 18, so no beat of theirs is rounded.
 * Editorial was to be 90, a beat of 20 frames: there a photo piece of two lines opening a run landed its first line a
 * frame off the grid at its shortest, and at 19 frames a voiced flip landed its first line a frame after its word. The
 * pieces' timing is one for every style and is not moved for one, so the tempo was; its calm is its music and its skin.
 */
export const STYLE_BPM: Record<VideoStyle, number> = { clean: 96, bold: 124, storybook: 78, notebook: 82, neon: 108, retro: 100, showreel: 96, editorial: 100, poster: 120, handmade: 100 };

/** The painted backdrops: a time of day and a place, each with its own painted sky and hills; a street and an office since 2026-09-26. */
export const Backdrop = z.enum(["dawn", "day", "night", "sea", "sunrise", "street", "office"]);
export type Backdrop = z.infer<typeof Backdrop>;

/** The painted cast. Where each one stands is the renderer's business; the model only says who is on stage. */
export const Prop = z.enum(["van", "shop", "houses", "person", "coins", "lighthouse", "document_in_sky", "desk", "parcels", "tree"]);
export type Prop = z.infer<typeof Prop>;

/**
 * What the person is doing (2026-09-26). The person is the same for a business in every scene and every video — their
 * look comes from its colours — so a story can follow one person from scene to scene.
 */
export const PersonPose = z.enum(["stand", "walk", "wave", "sit", "phone", "carry"]);
export type PersonPose = z.infer<typeof PersonPose>;

const Line = z.object({
  label: z.string().min(1).max(40),
  amount: z.string().max(16),
  /** Why this line is wrong, in two to four words ("charged twice"). Null when it is right. */
  flag: z.string().max(28).nullable(),
});

/** The most a headline holds: what the page check makes a headline of is cut to it, or it would not be drawn. */
const HEADLINE_MOST = 70;

export const HeadlineScene = z.object({
  kind: z.literal("headline"),
  eyebrow: z.string().max(40).nullable(),
  headline: z.string().min(1).max(HEADLINE_MOST),
  /** The words of the headline drawn in the accent colour, copied exactly from it. Null for none. */
  accent: z.string().max(40).nullable(),
});

export const BigNumberScene = z.object({
  kind: z.literal("big_number"),
  /** Counts up to `value`. A figure from the business page, never invented. */
  value: z.number(),
  prefix: z.string().max(4),
  suffix: z.string().max(12),
  caption: z.string().min(1).max(70),
});

export const DocumentCheckScene = z.object({
  kind: z.literal("document_check"),
  headline: z.string().min(1).max(60),
  /** What the document is ("Invoice 4471 · UPS", "Monday's rota"). */
  title: z.string().min(1).max(50),
  lines: z.array(Line).min(3).max(8),
  totalLabel: z.string().max(40),
  total: z.string().max(16),
});

export const StepsScene = z.object({
  kind: z.literal("steps"),
  headline: z.string().min(1).max(60),
  steps: z.array(z.object({ title: z.string().min(1).max(40), detail: z.string().max(50) })).min(2).max(4),
  /** A word stamped over the end ("CREDITED", "BOOKED"). Null for none. */
  stamp: z.string().max(24).nullable(),
});

export const OfferScene = z.object({
  kind: z.literal("offer"),
  headline: z.string().min(1).max(70),
  /** What the button says ("First month free"). */
  button: z.string().min(1).max(30),
  /** The website or phone number, as the business writes it. */
  address: z.string().max(40),
});

const Side = z.object({
  /** What this side is ("Without us", "Before", "Most auditors"). */
  label: z.string().min(1).max(24),
  points: z.array(z.string().min(1).max(36)).min(2).max(4),
});

/**
 * Two sides, the first crossed out and the second ticked: before and after, without and with, them and us
 * (docs/30 finding 10). A side's points are words, never a figure the page does not hold.
 */
export const CompareScene = z.object({
  kind: z.literal("compare"),
  headline: z.string().min(1).max(60),
  before: Side,
  after: Side,
});

export const PaintedScene = z.object({
  kind: z.literal("painted"),
  backdrop: Backdrop,
  cast: z.array(Prop).max(4),
  caption: z.string().min(1).max(50),
  sub: z.string().max(70).nullable(),
  /** With `document_in_sky`: the wrong lines on it, two to four words each. With `lighthouse` too, the beam finds them. */
  flags: z.array(z.string().min(1).max(24)).max(3),
  /** With `person`: what they are doing. Null is standing. `sit` sits them at the desk when there is one. */
  pose: PersonPose.nullable().default(null),
});

/**
 * The showreel pack's pieces (ADR 0058). This order is the render check's run (`sceneComposition`), not a video's: the
 * model is told the pieces in another (`SCENE_GUIDE`) and chooses its own story's (2026-09-29). The photo piece comes
 * before the close so the run still ends on one (2026-09-29).
 */
export const SHOWREEL_PIECES = ["hero", "reveal", "steps", "figure", "flip", "photo", "close"] as const;
export type ShowreelPiece = (typeof SHOWREEL_PIECES)[number];

/** The most lines a photo piece has, each pushing in on one region of its photo at most (2026-09-29). */
export const PHOTO_LINES_MAX = 2;

/**
 * The owner's own photo a photo piece shows (2026-09-29, ADR 0058 point 11): one of the photos the model was given, by
 * its id, and the region of it each line pushes in on, by the `n` its regions list gives it (0..11, as a tour stop's).
 * Both are choices from the list the model was given, so a scene is still only words, numbers and choices (ADR 0050).
 * A line past `regions` keeps the framing of the line before it, and a first line with none sees the whole photo, so
 * a photo with no regions read has `regions: []`.
 */
export const ShowreelPhoto = z.object({ id: z.uuid(), regions: z.array(z.number().int().min(0).max(11)).max(PHOTO_LINES_MAX) });
export type ShowreelPhoto = z.infer<typeof ShowreelPhoto>;

/**
 * A showreel piece (ADR 0058): a motion-designed scene drawn in the brand's accent and ink, with a bar that carries the
 * cut into the next piece. The model picks the piece and writes its lines; what each line is depends on the piece
 * (`SCENE_GUIDE`). A figure is only a figure piece's, written as it is shown: 30 and "%", or 1 and "M+" after "£".
 * A photo is only a photo piece's (2026-09-29): optional, so every scene stored before it still reads, and declared,
 * since zod leaves out a key it does not know. Null on a photo piece whose photo could not be named, which plays the
 * same moves on its own ground.
 */
export const ShowreelScene = z.object({
  kind: z.literal("showreel"),
  piece: z.enum(SHOWREEL_PIECES),
  lines: z.array(z.string().min(1).max(40)).min(1).max(3),
  figure: z.object({ value: z.number(), prefix: z.string().max(4), suffix: z.string().max(12) }).nullable(),
  photo: ShowreelPhoto.nullish(),
}).refine((s) => (s.piece === "photo" ? s.lines.length <= PHOTO_LINES_MAX && (s.photo?.regions.length ?? 0) <= s.lines.length : !s.photo), {
  message: "a photo piece has one or two lines and a region for each at most; no other piece has a photo",
});

export const Scene = z.discriminatedUnion("kind", [HeadlineScene, BigNumberScene, DocumentCheckScene, StepsScene, CompareScene, OfferScene, PaintedScene, ShowreelScene]);
export type Scene = z.infer<typeof Scene>;
export type HeadlineScene = z.infer<typeof HeadlineScene>;
export type BigNumberScene = z.infer<typeof BigNumberScene>;
export type DocumentCheckScene = z.infer<typeof DocumentCheckScene>;
export type StepsScene = z.infer<typeof StepsScene>;
export type OfferScene = z.infer<typeof OfferScene>;
export type CompareScene = z.infer<typeof CompareScene>;
export type PaintedScene = z.infer<typeof PaintedScene>;
export type ShowreelScene = z.infer<typeof ShowreelScene>;
export type SceneKind = Scene["kind"];
export const SCENE_KINDS = ["headline", "big_number", "document_check", "steps", "compare", "offer", "painted", "showreel"] as const satisfies readonly SceneKind[];

/** Whether a scene closes a video on its own: an offer, or a showreel close. No brand's card follows it (`compose`, docs/05). */
export const closesVideo = (scene: Scene | null | undefined) => scene?.kind === "offer" || (scene?.kind === "showreel" && scene.piece === "close");

/**
 * Words cut into the lines a piece sets, at its spaces: one line when it fits, else the two most even lines that do,
 * else as many lines of at most 40 characters as `most` allows. A word longer than a line is cut to it.
 */
export function showreelLines(text: string, most = 2): string[] {
  const t = text.trim().replace(/\s+/g, " ");
  if (!t) return [];
  if (t.length <= 40) return [t];
  const words = t.split(" ").map((w) => w.slice(0, 40));
  let best: string[] | null = null;
  for (let i = 1; i < words.length; i++) {
    const pair = [words.slice(0, i).join(" "), words.slice(i).join(" ")];
    if (pair.every((l) => l.length <= 40) && (!best || Math.max(...pair.map((l) => l.length)) < Math.max(...best.map((l) => l.length)))) best = pair;
  }
  if (best && most >= 2) return best;
  const out: string[] = [];
  for (const w of words) {
    const last = out.at(-1);
    if (last !== undefined && `${last} ${w}`.length <= 40) out[out.length - 1] = `${last} ${w}`;
    else out.push(w);
  }
  return out.slice(0, most);
}

/**
 * A scene as the showreel piece that says the same (2026-09-28): a Showreel video is showreel pieces from start to end,
 * so a card the model wrote into one is drawn as the piece that does its job, with its own words. A headline is a
 * hero (its accented words the payoff), a big number a figure, a checked document a reveal of its flags, steps are
 * steps, a before and after a flip of the after, an offer a close, and a painted picture a hero of its caption.
 */
export function asShowreel(scene: Scene): ShowreelScene {
  const piece = (p: ShowreelPiece, lines: string[], figure: ShowreelScene["figure"] = null): ShowreelScene =>
    ({ kind: "showreel", piece: p, lines: lines.length ? lines.slice(0, 3) : ["…"], figure });
  switch (scene.kind) {
    case "showreel": return scene;
    case "headline": {
      const a = scene.accent?.trim();
      const before = a && scene.headline.trim().endsWith(a) ? scene.headline.trim().slice(0, -a.length).trim() : "";
      return before && a!.length <= 40 ? piece("hero", [...showreelLines(before, 2), a!]) : piece("hero", showreelLines(scene.headline));
    }
    case "big_number": {
      // A figure piece reads two lines as the words before it and after it, so a caption that needs two would be cut
      // round the number: then the figure is said in words, at the head of a hero, and nothing is lost.
      if (scene.caption.trim().length <= 40) return piece("figure", [scene.caption.trim()], { value: scene.value, prefix: scene.prefix, suffix: scene.suffix });
      // Written short by hand: the locale's compact form is "1.2m" in the worker's Node and "1.2M" in a browser.
      const v = scene.value, a = Math.abs(v);
      const [by, unit] = a >= 1e9 ? [1e9, "bn"] : a >= 1e6 ? [1e6, "M"] : a >= 1e4 ? [1e3, "k"] : [1, ""];
      const short = unit ? `${+(v / by).toFixed(1)}${unit}` : v.toLocaleString("en-GB");
      return piece("hero", [`${scene.prefix}${short}${scene.suffix}`.trim().slice(0, 40), ...showreelLines(scene.caption, 2)]);
    }
    case "document_check": {
      const flags = scene.lines.flatMap((l) => (l.flag ? [l.flag] : [])).slice(0, 3);
      return flags.length ? piece("reveal", flags) : piece("hero", showreelLines(scene.headline));
    }
    case "steps": return piece("steps", scene.steps.slice(0, 3).map((x) => x.title));
    case "compare": return piece("flip", scene.after.points.slice(0, 3));
    case "offer": return piece("close", scene.address.trim() ? [scene.button, scene.address] : [scene.button]);
    case "painted": return piece("hero", scene.sub ? [...showreelLines(scene.caption, 2), ...showreelLines(scene.sub, 1)] : showreelLines(scene.caption));
  }
}

/** The library as the model is told it: one line per kind, what it draws and when it beats a photo. */
export const SCENE_GUIDE = `Drawn scenes (set "scene" on a shot instead of using a photo; leave it null otherwise):
- headline: a big line of words, an optional small eyebrow above, some of its words in the brand's accent. For a hook or a claim.
- big_number: a number counting up with a caption. Only a figure the business page states; a figure it does not state is drawn as a headline instead. "value" is the whole figure (1000000 for £1M, it is shortened on screen); "suffix" is only the unit or what follows it ("%", " lines", "+ a year").
- document_check: a document (an invoice, a quote, a rota, a menu) checked line by line, the wrong lines flagged, a total at the bottom. For a business whose work is checking or fixing things.
- steps: two to four steps appearing one after another, optionally stamped at the end. For how it works.
- compare: two sides, the first crossed out point by point and the second ticked: before and after, without and with, the usual way and this business's way. For the difference the business makes.
- offer: the close — a headline, a button with the offer, and the website or phone number.
- showreel: a motion-designed scene, bold and kinetic, in the brand's colours: big type slammed in letter by letter, a bar that moves from scene to scene. They cut into one another, so use them together (a run of three or more, or a whole video), not one among other kinds. Each piece carries one kind of beat, and a story uses only the pieces its beats need, in its own order. "piece" is one of: close (the end, once: [the business's name, the button's words, the website or phone number], or [button, address], or [button]); hero (one sentence a moment of the story rests on, the hook, the problem, the answer or a promise as the page makes it: one or two short lines, then the payoff line that lands under them; one sentence, never a list); photo (the work, the place or the product itself, in one of the owner's own photos: the frame opens on the whole photo and the camera pushes in on one region of it for each line, the line landing as the camera does; one or two very short statements of four words at most, each about the region it shows, like "Checked by hand." or "Every seal tested."; "photo" is {"id": the id of one of the photos you were given, "regions": [the n of the region each line shows, in order]}; only when you were given photos, each photo in one photo piece at most, for a beat a photo shows better than words, and a line says only what its region's label, the photo's caption or the page says, never what the photo might show, and never a number, a date or a price the page does not state, even one read off the photo); flip (one to three very short statements, each landing on its own ground as the ground cuts hard between light and dark: reasons, what is included, what the customer does not have to do, each as the page says it, like "No appointment needed." or "Open until eight."; two make a them-and-us or a before and after, and one alone is a turn, the ground flipping under it); figure (one heavy thing dropped onto a floor that bends under it: a figure the business page states, a price, a time, a count or a share, in "figure" as it is shown, {"value": 45, "prefix": "£", "suffix": ""}, or 1 and "M+" after "£" for £1M+, with lines [the words before it, the words after it] or [the words after it]; or, with "figure" null, the one or two words the whole story turns on, lines [before, the heavy words, after]; never a year or a phone number); steps (two or three steps in the order they really happen, each starting with its verb: how to book or buy, what happens to the customer's thing; reasons with no order belong on a flip); reveal (a page, a bill or a checklist, read by a scanner that stops on one to three of its lines, two to six words each, and rings them as wrong: what a check or an inspection turns up, the faults the business finds and fixes; only when the story has a check in it, never a menu or a price list, whose rows would read as mistakes, and never a promise). At most 40 characters a line; "figure" is null on every piece but a figure, and may be null there; "photo" is null on every piece but a photo, which always has one. A piece needs about 2.5 seconds, a reveal or steps with three lines about 3.7, a photo about 2.8, or 3.7 with two lines.
- painted: a painted storybook picture. A backdrop (dawn, day, night, sea, sunrise, street — a town street of shop fronts, office — indoors, a wall with a window onto the day) and up to four of: person, van (drives across), shop, houses, tree, parcels, desk (with a laptop), coins, lighthouse, document_in_sky. The person is the business's own character, the same in every scene: give them a "pose" (stand, walk, wave, sit — at the desk, phone, carry — a parcel) and follow them through the story. With document_in_sky, "flags" are the wrong lines written in the sky; with a lighthouse as well, its beam finds them and they turn into coins. For a story told in pictures.
Choose the few scenes that tell this business's story (three to five kinds is plenty), in the order the story needs. Do not use every kind or every showreel piece, and do not follow the order of either list: open on whatever makes a stranger stop (a painted picture, a number, a document with something wrong in it, a before and after, a bold line; in a Showreel video, a hero) and end on the offer (or a showreel close).`;

/** One valid example of every kind: the render check draws these, and a test holds the library to them. */
export const SCENE_SAMPLES: { [K in SceneKind]: Extract<Scene, { kind: K }> } = {
  headline: { kind: "headline", eyebrow: "Invoice 4471 · UPS", headline: "1,284 lines. Nobody checks them.", accent: "Nobody checks them." },
  big_number: { kind: "big_number", value: 1284, prefix: "", suffix: " lines", caption: "on one carrier invoice, every week" },
  document_check: {
    kind: "document_check", headline: "Every line checked.", title: "Invoice 4471 · UPS",
    lines: [
      { label: "Base charge", amount: "£5.80", flag: null },
      { label: "Contracted rate", amount: "£6.40", flag: "above your rate" },
      { label: "Fuel surcharge", amount: "£0.58", flag: null },
      { label: "Duplicate charge", amount: "£11.60", flag: "charged twice" },
      { label: "Residential", amount: "£2.10", flag: null },
    ],
    totalLabel: "Billed wrong", total: "£6.40",
  },
  steps: {
    kind: "steps", headline: "Then it gets the money back.",
    steps: [{ title: "Claim drafted", detail: "3 charges" }, { title: "Filed with the carrier", detail: "from your own account" }, { title: "Credit matched", detail: "on the next invoice" }],
    stamp: "CREDITED",
  },
  compare: {
    kind: "compare", headline: "The same invoice, twice.",
    before: { label: "Paid as billed", points: ["Duplicate charges", "Rates above contract", "Nobody checks"] },
    after: { label: "Checked first", points: ["Every line against your rate", "Claims filed for you", "Credit on the next invoice"] },
  },
  offer: { kind: "offer", headline: "We find it and get it back.", button: "First month free", address: "example.com" },
  painted: { kind: "painted", backdrop: "sea", cast: ["lighthouse", "document_in_sky"], caption: "We read every line.", sub: "against your contract", flags: ["charged twice", "wrong rate"], pose: null },
  showreel: { kind: "showreel", piece: "hero", lines: ["Nobody checks", "the invoices."], figure: null },
};

/** One valid example of every showreel piece, in the same made-up world: the render check draws them as one run. */
export const SHOWREEL_SAMPLES: { [P in ShowreelPiece]: ShowreelScene & { piece: P } } = {
  hero: { kind: "showreel", piece: "hero", lines: ["Nobody checks", "the invoices."], figure: null },
  reveal: { kind: "showreel", piece: "reveal", lines: ["Charged twice", "Above your contract rate", "A surcharge that is not due"], figure: null },
  steps: { kind: "showreel", piece: "steps", lines: ["Audit every line", "File the claim", "Track the credit back"], figure: null },
  figure: { kind: "showreel", piece: "figure", lines: ["We do it for you for", "of what comes back"], figure: { value: 30, prefix: "", suffix: "%" } },
  flip: { kind: "showreel", piece: "flip", lines: ["Independent.", "No labels sold.", "No commission."], figure: null },
  // A fixed id no photo is behind: the render check draws the piece on its own ground, as it does a photo gone.
  photo: { kind: "showreel", piece: "photo", lines: ["Every line, read."], figure: null, photo: { id: "0190a000-0000-7000-8000-000000000001", regions: [0] } },
  close: { kind: "showreel", piece: "close", lines: ["Example Freight Audit", "Book a call", "example.com"], figure: null },
};

/**
 * What the page holds, as every check asks it: whether a figure is on it (compared by its digits, and a big one also
 * as it is written short: £1M, 1.2k, 3bn), whether every figure in some words is, and whether a text is held — its
 * figures and its claims (2026-09-28). `known` is the page and the brief, as the model was given them. `dropped` is
 * told each sentence left out for a claim the page does not make, once.
 */
function pageHolds(known: string, dropped?: (sentence: string) => void) {
  const hay = `${known.toLowerCase().replace(/(\d),(?=\d{3})/g, "$1")} ${numbersInWords(known).join(" ")}`;
  // The one promise held by code (`NOTHING_ELSE`), sentence by sentence, so the sentences around one are kept. The
  // page is read the same way: read whole, a page that ended on a question was a question, and held nothing (review,
  // 2026-09-28).
  const page = sentencesOf(known).some(promisesNothingElse);
  const told = new Set<string>();
  const claimsHeld = (t: string) => page || sentencesOf(t).map((s) => {
    const ok = !promisesNothingElse(s);
    if (!ok && !told.has(s.trim())) { told.add(s.trim()); dropped?.(s.trim()); }
    return ok;
  }).every(Boolean);
  // A list's number is not a figure: "Tip #1" was left out (review, 2026-09-28).
  const digits = (s: string) => s.replace(/(\d),(?=\d{3})/g, "$1").replace(/#\d+\b/g, "").match(/\d+(?:\.\d+)?/g) ?? [];
  const has = (token: string) => new RegExp(`(^|[^\\d.])${token.replace(".", "\\.")}(?![\\d])`).test(hay);
  // A price is written with both digits of its pence: £3.20 was looked for as "3.2", which a café's page never writes,
  // and its figure piece became a headline (2026-09-29).
  const pence = (v: number) => (!Number.isInteger(v) && Number(v.toFixed(2)) === v ? [v.toFixed(2)] : []);
  const shortForms = (v: number) => [String(v), ...pence(v), ...[[1e9, "bn"], [1e9, "b"], [1e6, "m"], [1e3, "k"]].flatMap(([by, unit]) => {
    const n = v / (by as number);
    return n >= 1 && Number.isFinite(n) ? [`${+n.toFixed(1)}${unit}`, `${+n.toFixed(1)} ${unit === "m" ? "million" : unit === "k" ? "thousand" : "billion"}`] : [];
  })];
  const knownValue = (v: number) => shortForms(Math.abs(v)).some((f) => (/[a-z]/.test(f) ? hay.includes(f.toLowerCase()) : has(f)));
  const knownAmount = (a: string) => digits(a).every(has);
  return { knownValue, knownAmount, held: (t: string) => knownAmount(t) && claimsHeld(t) };
}

/**
 * A scene's claims held to what the business page and the brief say (2026-09-25). A big number is a claim, and so is
 * an amount on a document: one the page does not hold is not drawn — the number becomes a headline of its caption,
 * the amount is left empty. Then every word it shows is held (`wordsHeldTo`), for its figures and its claims.
 */
export function withKnownFigures(scene: Scene, known: string, fallback?: string, dropped?: (sentence: string) => void): Scene {
  const { knownValue, knownAmount, held } = pageHolds(known, dropped);
  // A figure's caption stands in for it only when it reads as a sentence of its own: "is overcharged by the carrier"
  // was on screen alone after its "1 in 50" was refused (dev box, 2026-09-26). Otherwise the shot's own words do.
  const standIn = (caption: string) => (fallback && !/^[\p{Lu}\p{N}£$€"“'‘]/u.test(caption.trim()) ? fallback : caption);
  const figures = (): Scene => {
    switch (scene.kind) {
      case "big_number":
        return knownValue(scene.value) ? scene : { kind: "headline", eyebrow: null, headline: standIn(scene.caption), accent: null };
      case "document_check":
        return { ...scene, total: knownAmount(scene.total) ? scene.total : "", lines: scene.lines.map((l) => (knownAmount(l.amount) ? l : { ...l, amount: "" })) };
      case "compare": {
        // A point with a figure or a claim the page does not hold is left out; a side left with fewer than two says
        // nothing. The usual way's points say what is wrong with it, not what the business promises ("Never a straight
        // answer"), so only their figures are held (review, 2026-09-28).
        const side = (x: CompareScene["before"], check: (t: string) => boolean) => ({ ...x, points: x.points.filter(check) });
        const before = side(scene.before, knownAmount), after = side(scene.after, held);
        return before.points.length >= 2 && after.points.length >= 2 ? { ...scene, before, after } : { kind: "headline", eyebrow: null, headline: scene.headline, accent: null };
      }
      case "showreel": {
        // Only a figure piece shows a figure. One the page does not state turns it into a headline of the shot's own
        // words, as a big number does; written short (1 and "M+"), it is looked for as the whole figure.
        if (!scene.figure) return scene;
        if (scene.piece !== "figure") return { ...scene, figure: null };
        const short = /^(bn|[KkMB])(?![a-z])/.exec(scene.figure.suffix);
        const value = short ? scene.figure.value * ({ k: 1e3, K: 1e3, M: 1e6, B: 1e9, bn: 1e9 } as const)[short[1] as "k" | "K" | "M" | "B" | "bn"] : scene.figure.value;
        return knownValue(value) ? scene : { kind: "headline", eyebrow: null, headline: fallback?.trim() ? fallback : scene.lines.join(" "), accent: null };
      }
      default:
        return scene;
    }
  };
  return wordsHeldTo(figures(), held, knownAmount, fallback);
}

/**
 * A spoken line held to the page as the words on screen are (2026-09-28): the sentences of it whose figures and claims
 * the page holds, or null when none are, so the voice cannot say what the screen leaves out. It is held in every
 * style, since the voice speaks in all of them.
 */
export function heldSpeech(text: string, known: string, dropped?: (sentence: string) => void): string | null {
  const { held } = pageHolds(known, dropped);
  const said = (x: string) => held(x) && wordFiguresHeld(x, known);
  const t = text.trim();
  return (said(t) ? t : sentencesOf(t).filter(said).join("").trim()) || null;
}

/**
 * A figure said or drawn in words is a figure ("over fifty years"), from five up: "two hands, one oven" claims nothing.
 * Asked of what is spoken and of a picture's own words. A scene's fallback keeps its own rule (`withKnownFigures`).
 */
export function wordFiguresHeld(text: string, known: string): boolean {
  const on = new Set([...numbersInWords(known), ...(known.replace(/(\d),(?=\d{3})/g, "$1").match(/\d+(?:\.\d+)?/g) ?? []).map(Number)]);
  return numbersInWords(text).filter((n) => n >= WORD_FIGURE_FROM).every((n) => on.has(n));
}

const UNITS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const SCALES: Record<string, number> = { hundred: 100, thousand: 1_000, million: 1_000_000 };

/**
 * The figures a text writes in words, as numbers: "one line in fifty" holds 1 and 50, "two hundred" 200. A brief that
 * says "fifty" makes a drawn "50" one the business gave (dev box, 2026-09-26).
 */
export function numbersInWords(text: string): number[] {
  const word = `(${[...UNITS, ...TENS.filter(Boolean)].join("|")})`;
  const re = new RegExp(`\\b${word}(?:[-\\s]${word})?(?:\\s(hundred|thousand|million))?\\b`, "gi");
  const out: number[] = [];
  for (const m of text.matchAll(re)) {
    const v = (w: string | undefined) => (w ? Math.max(UNITS.indexOf(w.toLowerCase()), TENS.indexOf(w.toLowerCase()) * 10) : 0);
    const a = v(m[1]), b = m[2] && TENS.includes(m[1]!.toLowerCase()) && UNITS.indexOf(m[2].toLowerCase()) < 10 ? v(m[2]) : 0;
    out.push((a + b) * (m[3] ? SCALES[m[3].toLowerCase()]! : 1));
  }
  return out;
}

/**
 * A figure as it is written in words on screen: a sign, the digits, and what follows them (%, k, m, million, +). A
 * list's number is not one: "Tip #12" lost its 12 (review, 2026-09-28).
 */
const FIGURE = /(?<![#\d])[£$€]?\d[\d,]*(?:\.\d+)?\s*(?:%|k\b|m\b|bn\b|million|thousand|billion)?\+?/gi;

/**
 * A text's sentences, each with its stop and the space after it, so the ones kept join back as they were written. A
 * stop ends a sentence only before a space or the end: "£2.50", "7.30am" and "example.co.uk" were cut at their dot and
 * said as "£2", "7" and "example.co" (review, 2026-09-28). A new line ends one too, as a page's lines are its items.
 */
const SENTENCE = /(?:[^.!?]|[.!?]+(?=[^\s.!?]))+[.!?]*\s*|[.!?]+\s*/g;
const sentencesOf = (t: string) => t.split(/(?<=\n)/).flatMap((l) => l.match(SENTENCE) ?? []);

/**
 * The one promise held to the page by what it says (2026-09-28): that there is nothing else to pay. The owner's first
 * Showreel video said "30%", then "of what comes back. Nothing else.", and the page gives the 30% but never says
 * "nothing else". Two richer checks were built and measured on batteries of made-up small businesses (a café, a
 * plumber, a bike shop, a groomer, a florist): a list of promise words left out a quarter of the lines it should have
 * kept ("Get the best out of your bike"), and six kinds of promise matched by meaning still lost 7 good sentences in
 * 100 on a battery it had never seen, let 31 invented promises through and read "always" as "alway" (review,
 * 2026-09-28). A line wrongly left out silences a shot or blanks a heading, which is worse than a boast let through,
 * so code holds only this promise, the one a price invites and the one a video made up; every other promise is the
 * prompt's to keep out.
 *
 * Its ways: "nothing else" or "nothing more" where what is said ends or before "to pay"; no other, extra, hidden,
 * additional, further or upfront fee, cost or charge; nothing (to pay) upfront. The page holds it when it says it in
 * any of them. "Nothing else quite like a warm brownie", "Nothing more, nothing less" and "No two carriers charge the
 * same" promise nothing, and a question promises nothing. Something at no extra charge, or no extra charge for
 * something, is that thing offered free, which is the prompt's too: "Oat milk at no extra charge" was left out of a
 * page that gives oat milk free (review, 2026-09-28).
 */
const NOTHING_ELSE = [
  /\bnothing (?:else|more)(?!,? nothing less)(?=\s*(?:[.!,;:)\u2013\u2014]|$|to pay\b|on top\b|charged\b))/i,
  /\bno (?:other|hidden|further|up-?front) (?:fees?|costs?|charges?)\b/i,
  /(?<!\bat )\bno (?:extra|additional) (?:fees?|costs?|charges?)\b(?! for\b)/i,
  /\bnothing (?:to pay )?up-? ?front\b/i,
];
const WORD_FIGURE_FROM = 5;

const promisesNothingElse = (t: string) => !/\?\W*$/.test(t.trim()) && NOTHING_ELSE.some((re) => re.test(t.replace(/\s+/g, " ")));

/**
 * Every word a scene puts on screen held to the page (2026-09-26): an offer said "You keep 70% of the recovery", worked
 * out from the page's 30% but written nowhere, on two videos in a row. A sentence with a figure the page does not
 * hold is left out; when that leaves nothing, the figure alone is. A sentence with a claim the page does not make is
 * left out the same way (2026-09-28), but is never kept whole: a heading left with nothing says the shot's own words,
 * held too, or nothing. A button with either says "Get in touch", and an address with one is left empty, since both
 * are only worth having when they are exactly right. What a scene says is wrong — a document's flags, a painted sky's
 * — is not what the business promises, so only its figures are held (`figures`). A reveal's lines were held that way
 * too, until a reveal could be the picks from a list as well as what a check turns up (2026-09-29): then it can say
 * what the business offers, and is held as every other piece is.
 */
function wordsHeldTo(scene: Scene, known: (t: string) => boolean, figures: (t: string) => boolean, fallback?: string): Scene {
  const sentences = (t: string, check = known) => sentencesOf(t).filter(check).join("").trim();
  const instead = (fallback && sentences(fallback)) || "…";
  // A headline made of what was kept is only drawn when it fits one (review, 2026-09-28: 81 characters).
  const fit = (t: string) => (t.length <= HEADLINE_MOST ? t : instead);
  // Words a scene must have keep what they can; words it may leave out go whole, never as a fragment ("Since").
  const clean = (t: string, last = "…") => {
    if (known(t)) return t;
    const bare = t.replace(FIGURE, (m) => (known(m) ? m : "")).replace(/\s{2,}/g, " ").replace(/\s+([.,!?;:])/g, "$1").trim();
    return sentences(t) || (known(bare) ? bare : "") || last;
  };
  const maybe = (t: string | null, check = known) => (t === null || check(t) ? t : sentences(t, check) || null);
  switch (scene.kind) {
    case "headline": {
      const headline = clean(fit(scene.headline), instead);
      return { ...scene, headline, eyebrow: maybe(scene.eyebrow), accent: scene.accent && known(scene.accent) && headline.includes(scene.accent) ? scene.accent : null };
    }
    case "big_number": {
      // A number with none of its caption kept says the shot's own words, as one the page does not hold does.
      const caption = clean(scene.caption, "");
      return caption ? { ...scene, caption } : { kind: "headline", eyebrow: null, headline: instead, accent: null };
    }
    case "document_check":
      return { ...scene, headline: clean(scene.headline), title: clean(scene.title), totalLabel: known(scene.totalLabel) ? scene.totalLabel : "",
        lines: scene.lines.map((l) => ({ ...l, label: clean(l.label), flag: maybe(l.flag, figures) })) };
    case "steps":
      return { ...scene, headline: clean(scene.headline), stamp: maybe(scene.stamp), steps: scene.steps.map((x) => ({ title: clean(x.title), detail: maybe(x.detail) ?? "" })) };
    case "compare": return { ...scene, headline: clean(scene.headline), before: { ...scene.before, label: clean(scene.before.label) }, after: { ...scene.after, label: clean(scene.after.label) } };
    case "offer": return { ...scene, headline: clean(scene.headline, instead), button: known(scene.button) ? scene.button : "Get in touch", address: known(scene.address) ? scene.address : "" };
    case "painted": return { ...scene, caption: clean(scene.caption), sub: maybe(scene.sub), flags: scene.flags.filter(figures) };
    case "showreel": {
      // A close's lines are its name, button and address by how many there are, so a line left out is taken out of its
      // place, not the list: a button with a figure or a claim the page does not hold says "Get in touch", as an
      // offer's does.
      if (scene.piece === "close") {
        const [a, b, c] = scene.lines;
        const [name, button, address] = scene.lines.length >= 3 ? [a!, b!, c!] : scene.lines.length === 2 ? [null, a!, b!] : [null, a!, null];
        const kept = { name: name && known(name) ? name : null, button: known(button) ? button : "Get in touch", address: address && known(address) ? address : null };
        return { ...scene, lines: kept.address ? (kept.name ? [kept.name, kept.button, kept.address] : [kept.button, kept.address]) : [kept.button] };
      }
      // A hero's lines are one sentence broken across lines, the last its payoff: one left out left half a sentence on
      // screen and in the voice, and the line above it drawn as the payoff (review, 2026-09-28). So a hero is held as
      // the sentence it is, and is kept whole or says the shot's own words.
      if (scene.piece === "hero") return known(scene.lines.join(" ")) ? scene : { kind: "headline", eyebrow: null, headline: instead, accent: null };
      // Any other piece keeps the lines it can, each where it was. A figure piece's lines are what they are by how many
      // there are (`figureText`: the line above, then the caption; with no figure, the heavy line between them), so one
      // that would read a kept line in another's place — its line above as its caption, when the caption went — is a
      // headline of the words that were kept, the figure where it stood ("We fix 90%"). So is a piece left with too few
      // lines (steps need two); one left with none says the shot's own words.
      const kept = scene.lines.map((l) => (known(l) ? l : sentences(l) || null));
      const at = kept.flatMap((l, i) => (l ? [i] : []));
      const role = (i: number, of: number) => (of < 2 ? "main" : i === 0 ? "above" : scene.figure || i === 1 ? "main" : "caption");
      const inPlace = scene.piece !== "figure" || at.every((i, j) => role(i, scene.lines.length) === role(j, at.length));
      // A photo piece's lines are held one by one, as a flip's are, and each kept line keeps the framing it had: its own
      // region, or the one before it when it had none (2026-09-29).
      const framed = (r: number[]) => (r.length ? [...new Set(at.map((i) => r[Math.min(i, r.length - 1)]!))] : []);
      if (inPlace && at.length >= (scene.piece === "steps" ? 2 : 1)) return { ...scene, lines: at.map((i) => kept[i]!), ...(scene.photo ? { photo: { ...scene.photo, regions: framed(scene.photo.regions) } } : {}) };
      const f = scene.figure, fAt = scene.lines.length >= 2 ? 1 : 0;
      const words = kept.flatMap((l, i) => [...(f && i === fAt ? [`${f.prefix}${f.value}${f.suffix}`] : []), ...(l ? [l] : [])]);
      return { kind: "headline", eyebrow: null, headline: at.length ? fit(clean(words.join(" "), instead)) : instead, accent: null };
    }
  }
}

/** What the Studio calls each kind, on a shot card and in the editor. */
export const SCENE_LABEL: Record<SceneKind, string> = {
  headline: "Headline", big_number: "Big number", document_check: "Document checked", steps: "Steps", compare: "Before and after", offer: "Offer", painted: "Painted",
  showreel: "Showreel",
};

/** The words a scene puts on screen, in one line: how a drawn shot is shown where a photo would be a thumbnail. */
export function sceneWords(s: Scene): string {
  switch (s.kind) {
    case "headline": return s.headline;
    // Shortened as the video shows it: £1M, not £1000000 (the Studio showed the long form, 2026-09-25).
    case "big_number": return `${s.prefix}${Math.abs(s.value) >= 10_000 ? s.value.toLocaleString("en-GB", { notation: "compact", maximumFractionDigits: 1 }) : s.value}${s.suffix} ${s.caption}`;
    case "document_check": return s.headline;
    case "steps": return s.headline;
    case "compare": return `${s.headline} ${s.before.label} / ${s.after.label}`;
    case "offer": return `${s.headline} ${s.button}`;
    case "painted": return `${s.caption} (${s.backdrop}${s.cast.length ? `: ${s.cast.join(", ").replaceAll("_", " ")}` : ""})`;
    case "showreel": return [s.figure ? `${s.figure.prefix}${s.figure.value}${s.figure.suffix}` : "", ...s.lines].filter(Boolean).join(" · ");
  }
}

/**
 * A scene the model wrote as JSON text, read into the library's shape, or null when it is not one of them. The model
 * writes a scene as a string because a union of every scene inside every shot compiled into a structured-output
 * grammar the API refused as too large (dev box, 2026-09-25). Null is safe: the shot is a picture, and a drawn
 * video turns it into a headline of its own words.
 */
/**
 * Why the library refused a scene the model wrote, as the paths and rules it broke and never the words: a shot left
 * without its scene falls back to a plain headline, and without this nobody could tell why (dev box, 2026-09-26).
 */
export function sceneRefusal(text: string | null): string | null {
  if (!text) return null;
  let json: unknown;
  try { json = JSON.parse(text); } catch { return "not JSON"; }
  const parsed = Scene.safeParse(json);
  if (parsed.success) return null;
  const kind = json && typeof json === "object" && "kind" in json ? String((json as { kind: unknown }).kind).slice(0, 20) : "?";
  return `${kind}: ${parsed.error.issues.slice(0, 4).map((i) => `${i.path.join(".") || "(scene)"} ${i.code}`).join("; ")}`;
}

/**
 * The parts of a scene it can do without. One of these written too long is left out rather than the whole scene: a
 * steps scene was lost to a stamp of 35 characters, and its shot became a plain headline (dev box, 2026-09-26).
 */
const OPTIONAL = new Set(["stamp", "eyebrow", "sub", "accent", "flag"]);

function withoutOverlong(json: unknown, issues: ReadonlyArray<{ code: string; path: PropertyKey[] }>): unknown {
  const fixable = issues.filter((i) => i.code === "too_big" && OPTIONAL.has(String(i.path.at(-1))));
  if (!fixable.length || fixable.length !== issues.length) return null;
  const copy = structuredClone(json) as Record<string, unknown>;
  for (const i of fixable) {
    let at: Record<string, unknown> = copy;
    for (const k of i.path.slice(0, -1)) at = at[k as string] as Record<string, unknown>;
    at[i.path.at(-1) as string] = null;
  }
  return copy;
}

/**
 * A showreel scene as the model wrote it, made to fit before it is read: blank lines out, a line too long to set left
 * out rather than the whole scene, no more than three lines, and a figure only on a figure piece, and only one it
 * knows ("?" is not a figure). A photo only on a photo piece, which keeps its first two lines (2026-09-29): one it
 * cannot name is null rather than the scene lost, and the worker makes the piece a hero of its lines (`photoPieces`).
 */
function fittedShowreel(json: unknown): unknown {
  if (!json || typeof json !== "object" || (json as { kind?: unknown }).kind !== "showreel") return json;
  const s = { ...(json as Record<string, unknown>) };
  const photo = s.piece === "photo";
  // Where each line kept was in the model's list, which is what its region is matched by (review, 2026-09-29).
  let kept: number[] = [];
  if (Array.isArray(s.lines)) {
    const lines = s.lines.map((l) => (typeof l === "string" ? l.trim() : l));
    kept = lines.flatMap((l, i) => (typeof l === "string" && l && l.length <= 40 ? [i] : [])).slice(0, photo ? PHOTO_LINES_MAX : 3);
    s.lines = kept.map((i) => lines[i]);
  }
  const f = s.figure as { prefix?: unknown; suffix?: unknown } | null | undefined;
  if (s.piece !== "figure" || !f || [f.prefix, f.suffix].some((x) => typeof x === "string" && x.includes("?"))) s.figure = null;
  else s.figure = { ...f, prefix: typeof f.prefix === "string" ? f.prefix : "", suffix: typeof f.suffix === "string" ? f.suffix : "" };
  if (photo) s.photo = namedPhoto(s.photo, kept);
  else delete s.photo;
  return s;
}

/**
 * The photo a photo piece names, or null: an id that is one, and the region of each line kept, `kept` being where each
 * was in the model's list, as the model gave it to that very line. They stop at the first line given no region it can
 * have, or the region of the line before it again: that line and every one after it keep the framing before them. A
 * region left out rather than stopped at moved the next line's onto it, and "Every gauge read." pushed in on the label
 * the line after it was about (review, 2026-09-29).
 */
function namedPhoto(p: unknown, kept: readonly number[]): ShowreelPhoto | null {
  if (!p || typeof p !== "object") return null;
  const { id, regions } = p as { id?: unknown; regions?: unknown };
  if (typeof id !== "string" || !ShowreelPhoto.shape.id.safeParse(id).success) return null;
  const given: unknown[] = Array.isArray(regions) ? regions : [];
  const out: number[] = [];
  for (const i of kept) {
    const n = given[i];
    if (typeof n !== "number" || !Number.isInteger(n) || n < 0 || n > 11 || n === out.at(-1)) break;
    out.push(n);
  }
  return { id, regions: out };
}

export function sceneFromModel(text: string | null): Scene | null {
  if (!text) return null;
  try {
    const json: unknown = fittedShowreel(JSON.parse(text));
    const first = Scene.safeParse(json);
    const parsed = first.success ? first : Scene.safeParse(withoutOverlong(json, first.error.issues));
    if (!parsed.success) return null;
    // A figure the model did not know, written as "?", is left out rather than drawn (dev box, 2026-09-25).
    const scene = parsed.data;
    const known = (v: string) => (/^[\s?]*$/.test(v) ? "" : v);
    // "£1M" written as a value of 1 and a suffix of "M…" counted from £0 to £1 on the dev box (2026-09-25).
    const scaled = scene.kind === "big_number" ? /^(bn|[KkMB])(?![a-z])/.exec(scene.suffix) : null;
    if (scene.kind === "big_number" && scaled) {
      const by = { k: 1e3, K: 1e3, M: 1e6, B: 1e9, bn: 1e9 }[scaled[1]!]!;
      return { ...scene, value: scene.value * by, suffix: scene.suffix.slice(scaled[1]!.length) };
    }
    if (scene.kind === "document_check") return { ...scene, total: known(scene.total), lines: scene.lines.map((l) => ({ ...l, amount: known(l.amount) })) };
    return scene;
  } catch {
    return null;
  }
}
